<script setup>
/* global axios */
// Р18/Р19: конфигурация источника-мессенджера. Telegram/MAX — один токен бота;
// ВКонтакте — сообщество с несколькими полями (ID сообщества, ключ доступа,
// строка подтверждения Callback, секретный ключ). Показываем текущего
// бота/сообщество и даём заменить настройки (переключить тест→прод). Бэкенд
// валидирует при сохранении (getMe/me/groups.getById), обновляет имя и
// переустанавливает webhook/Callback; невалидные данные → ошибка, старые
// настройки остаются.
import { ref, reactive, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const isSaving = ref(false);
// Объект инбокса в сторе бывает из устаревшего клиентского кэша — тянем свежий
// напрямую по API (минуя кэш), чтобы имя/поля были актуальны (Р19 п.1).
const freshInbox = ref(null);

const isVk = computed(() => props.inbox.channel_type === 'Channel::Vk');
const source = computed(() => freshInbox.value || props.inbox);

const currentLabel = computed(() =>
  isVk.value
    ? t('INBOX_MGMT.BOT_CONFIG.CURRENT_COMMUNITY')
    : t('INBOX_MGMT.BOT_CONFIG.CURRENT_BOT')
);

const currentName = computed(() => {
  const name = source.value.bot_name || source.value.group_name || '';
  if (!name) return t('INBOX_MGMT.BOT_CONFIG.UNKNOWN');
  return isVk.value ? name : `@${name}`;
});

// Telegram/MAX: один токен.
const newToken = ref('');

// ВКонтакте: полный набор полей (несекретные префилятся, секреты — пусто).
const vkForm = reactive({
  group_id: '',
  access_token: '',
  confirmation_code: '',
  secret_key: '',
});

const fetchFreshInbox = async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}`
    );
    freshInbox.value = data;
    if (isVk.value) {
      vkForm.group_id = data.group_id || '';
      vkForm.confirmation_code = data.confirmation_code || '';
    }
  } catch {
    // остаёмся на данных из props
  }
};

onMounted(fetchFreshInbox);

const applyUpdate = async channel => {
  isSaving.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      name: props.inbox.name,
      channel,
    });
    await fetchFreshInbox();
    useAlert(t('INBOX_MGMT.BOT_CONFIG.SUCCESS'));
    return true;
  } catch (error) {
    useAlert(error?.message || t('INBOX_MGMT.BOT_CONFIG.ERROR'));
    return false;
  } finally {
    isSaving.value = false;
  }
};

const saveToken = async () => {
  const token = newToken.value.trim();
  if (!token) return;
  if (await applyUpdate({ bot_token: token })) newToken.value = '';
};

const canSaveVk = computed(
  () =>
    vkForm.group_id.trim() && (vkForm.access_token.trim() || freshInbox.value)
);

const saveVk = async () => {
  const channel = {
    group_id: vkForm.group_id.trim(),
    confirmation_code: vkForm.confirmation_code.trim(),
  };
  // Секреты отправляем, только если введены — пусто = оставить прежние.
  if (vkForm.access_token.trim())
    channel.access_token = vkForm.access_token.trim();
  if (vkForm.secret_key.trim()) channel.secret_key = vkForm.secret_key.trim();
  if (await applyUpdate(channel)) {
    vkForm.access_token = '';
    vkForm.secret_key = '';
  }
};
</script>

<template>
  <div class="flex flex-col gap-6 py-4 mx-6 max-w-4xl">
    <div class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-12">{{
        currentLabel
      }}</span>
      <span class="text-base text-n-slate-12">{{ currentName }}</span>
    </div>

    <!-- ВКонтакте: полный набор полей сообщества -->
    <div v-if="isVk" class="flex flex-col gap-4 max-w-lg">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.BOT_CONFIG.VK_TITLE') }}
      </span>
      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.VK_CHANNEL.GROUP_ID.LABEL') }}
        </span>
        <input
          v-model="vkForm.group_id"
          type="text"
          class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-12"
          :placeholder="t('INBOX_MGMT.ADD.VK_CHANNEL.GROUP_ID.PLACEHOLDER')"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.VK_CHANNEL.ACCESS_TOKEN.LABEL') }}
        </span>
        <input
          v-model="vkForm.access_token"
          type="text"
          autocomplete="off"
          class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-12"
          :placeholder="t('INBOX_MGMT.BOT_CONFIG.LEAVE_BLANK')"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.VK_CHANNEL.CONFIRMATION.LABEL') }}
        </span>
        <input
          v-model="vkForm.confirmation_code"
          type="text"
          class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-12"
          :placeholder="t('INBOX_MGMT.ADD.VK_CHANNEL.CONFIRMATION.PLACEHOLDER')"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.VK_CHANNEL.SECRET_KEY.LABEL') }}
        </span>
        <input
          v-model="vkForm.secret_key"
          type="text"
          autocomplete="off"
          class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-12"
          :placeholder="t('INBOX_MGMT.BOT_CONFIG.LEAVE_BLANK')"
        />
      </label>
      <p class="text-xs text-n-slate-11">
        {{ t('INBOX_MGMT.ADD.VK_CHANNEL.SUBTITLE') }}
      </p>
      <div>
        <NextButton
          :label="t('INBOX_MGMT.BOT_CONFIG.SAVE')"
          :is-loading="isSaving"
          :disabled="isSaving || !canSaveVk"
          @click="saveVk"
        />
      </div>
    </div>

    <!-- Telegram / MAX: один токен бота -->
    <div v-else class="flex flex-col gap-2 max-w-lg">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.BOT_CONFIG.NEW_TOKEN_LABEL') }}
      </label>
      <p class="text-xs text-n-slate-11">
        {{ t('INBOX_MGMT.BOT_CONFIG.NEW_TOKEN_HELP') }}
      </p>
      <input
        v-model="newToken"
        type="text"
        autocomplete="off"
        class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-12"
        :placeholder="t('INBOX_MGMT.BOT_CONFIG.NEW_TOKEN_PLACEHOLDER')"
      />
      <div class="mt-1">
        <NextButton
          :label="t('INBOX_MGMT.BOT_CONFIG.SAVE')"
          :is-loading="isSaving"
          :disabled="isSaving || !newToken.trim()"
          @click="saveToken"
        />
      </div>
    </div>
  </div>
</template>
